import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    ViewChild,
    OnInit,
    AfterViewInit,
    OnDestroy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import moment from 'moment';
import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    DISPLAY_TIME_FORMATS,
    READONLY_DATE_FORMATS,
    TimePrecision,
    MaskSettings,
    popperDateTimeOptions,
    TimeintervalSettingsParts,
} from '@bazis/form/models/form-element.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SimpleData } from '@bazis/shared/models/srv.types';
import { createPopper, Instance } from '@popperjs/core';
import { DateValidators } from '@bazis/form/validators/dateValidators';
import { COMMON_DATE_MASK_SETTINGS, API_DATETIME_FORMAT } from '@bazis/configuration.service';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { ConditionalValidators } from '@bazis/form/validators/conditionalValidators';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-single-input-timeinterval',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisSingleInputTimeintervalComponent
    extends BazisControlValueAccessor
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('timeInput') timeInput: ElementRef;

    @ViewChild('timePopper') timePopper: ElementRef;

    @Input() defaultInterval: boolean = true;

    @Input() inputFormat: string = API_DATETIME_FORMAT;

    @Input() precision: TimePrecision = 'minutes';

    @Input() showIntervalCheckbox = true;

    @Input() showDayAndNightCheckbox = true;

    @Input() fieldSettingsParts: TimeintervalSettingsParts = null;

    disabled = false;

    value = new FormControl();

    isDayAndNight = new FormControl([]);

    isInterval = new FormControl(['interval']);

    calendarValue: Date[];

    displayFormat: string;

    displayMaskFormat: string;

    displayIntervalMaskFormat: string;

    readonlyFormat: string;

    showCalendar = false;

    mask = '';

    intervalMask = '';

    delimiter = '–';

    dayAndNightValue = '';

    isIntervalOptions: SimpleData[] = [
        {
            id: 'interval',
            nameKey: 'title.range',
        },
    ];

    isDayAndNightOptions: SimpleData[] = [
        {
            id: 'dayAndNight',
            nameKey: 'title.dayAndNight',
        },
    ];

    needUpdateMaskSettings$ = new BehaviorSubject(null);

    maskSettings$: Observable<MaskSettings> = this.needUpdateMaskSettings$.pipe(
        map(() => {
            return this.isInterval.value.length > 0
                ? {
                      ...COMMON_DATE_MASK_SETTINGS,
                      mask: this.intervalMask,
                  }
                : {
                      ...COMMON_DATE_MASK_SETTINGS,
                      mask: this.mask,
                  };
        }),
    );

    instanceTimePopper: Instance;

    extendOnInit() {
        this.displayFormat = DISPLAY_TIME_FORMATS[this.precision];
        this.displayMaskFormat = `${this.displayFormat}`;
        this.displayIntervalMaskFormat = `${this.displayFormat}${this.delimiter}${this.displayFormat}`;
        this.mask = this.displayMaskFormat.replace(/[A-Za-z]/g, '0');
        this.intervalMask = this.displayIntervalMaskFormat.replace(/[A-Za-z]/g, '0');
        this.dayAndNightValue = this._calculateDayAndNightValue();
        this.readonlyFormat = READONLY_DATE_FORMATS[this.precision];
        this.value.valueChanges
            .pipe(
                tap(() => {
                    this.setCalendarValues();
                    this.setControlValue();
                    if (
                        this.isDayAndNight.value.length > 0 &&
                        this.value.value !== this.dayAndNightValue
                    ) {
                        this.isDayAndNight.setValue([]);
                    }
                    if (
                        !this.isDayAndNight.value.length &&
                        this.value.value === this.dayAndNightValue
                    ) {
                        this.isDayAndNight.setValue(['dayAndNight']);
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.value.statusChanges
            .pipe(
                tap((status) => {
                    if (status === 'VALID') {
                        let errors = this.ngControl.control.errors;
                        if (!errors) return;
                        delete errors.componentError;
                        errors = Object.keys(errors).length > 0 ? errors : null;
                        this.ngControl.control.setErrors(errors);
                    } else {
                        this.ngControl.control.setErrors({
                            ...this.ngControl.control.errors,
                            componentError: { ...this.value.errors },
                        });
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
        this.isDayAndNight.valueChanges
            .pipe(
                tap(() => {
                    if (this.isDayAndNight.value && this.isDayAndNight.value.length > 0) {
                        this.isInterval.setValue(['interval'], { emitEvent: false });
                        this.needUpdateMaskSettings$.next(null);
                        this.value.setValue(this.dayAndNightValue);
                        this.isInterval.disable();
                    } else {
                        this.isInterval.enable();
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
        this.isInterval.valueChanges
            .pipe(
                tap(() => {
                    const isInterval = this.isInterval.value.length > 0;
                    let value = this.value.value ? this.value.value.split(this.delimiter) : [''];
                    if (value.length === 1) {
                        value.push('');
                    }
                    if (!isInterval) {
                        this.isDayAndNight.setValue([], { emitEvent: false });
                    }
                    this.initValidators();

                    this.value.setValue(
                        isInterval ? (value[0] ? value.join(this.delimiter) : '') : value[0],
                    );
                    this.needUpdateMaskSettings$.next(null);
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.initValidators();
    }

    ngOnInit() {
        super.ngOnInit();
        this.value.updateValueAndValidity();
    }

    ngAfterViewInit() {
        const reference = this.timeInput.nativeElement;
        const popper = this.timePopper.nativeElement;

        this.instanceTimePopper = createPopper(reference, popper, {
            ...popperDateTimeOptions,
        });
    }

    ngOnDestroy() {
        this.instanceTimePopper.destroy();
    }

    initValidators() {
        const validators = [];
        if (this.ngControl.control instanceof EntityFormControl) {
            validators.push(
                ConditionalValidators.required(
                    () => (this.ngControl.control as EntityFormControl).$config.required?._,
                ),
            );
        }
        if (this.isInterval.value.length > 0) {
            validators.push(DateValidators.intervalTimeValidatorFn(this.delimiter));
        } else {
            validators.push(DateValidators.invalidTimeValidatorFn());
        }
        this.value.setValidators(validators);
    }

    getMomentTime(part: string) {
        const momentTime = moment(part, this.displayFormat, true);
        return momentTime && momentTime.isValid()
            ? moment()
                  .hours(momentTime.hours())
                  .minutes(momentTime.minutes())
                  .seconds(momentTime.seconds())
                  .milliseconds(momentTime.milliseconds())
            : null;
    }

    getMomentValues() {
        const parts = this.value.value ? this.value.value.split(this.delimiter) : [];

        return {
            from: parts[0] ? this.getMomentTime(parts[0]) : null,
            to: parts[1] ? this.getMomentTime(parts[1]) : null,
            fromPart: parts[0],
            toPart: parts[1],
        };
    }

    setCalendarValues() {
        const momentValues = this.getMomentValues();
        this.calendarValue = [
            momentValues.from ? momentValues.from.toDate() : null,
            momentValues.to ? momentValues.to.toDate() : null,
        ];
    }

    setControlValue() {
        const momentValues = this.getMomentValues();
        let value = [null, null];
        if (momentValues.from || momentValues.fromPart) {
            value[0] = momentValues.from ? momentValues.from.format(this.inputFormat) : null; // null = invalid
        }
        if (momentValues.to || momentValues.toPart) {
            value[1] = momentValues.to ? momentValues.to.format(this.inputFormat) : null; // null = invalid
        }

        if (!value[0] && !value[1]) {
            value = [];
        }
        if (this.fieldSettingsParts) {
            this.onChange({
                [this.fieldSettingsParts.fromTimeFieldName]:
                    value.length > 0 && value[0]
                        ? moment(value[0], this.inputFormat).format(
                              this.fieldSettingsParts.timeFormat,
                          )
                        : null,
                [this.fieldSettingsParts.toTimeFieldName]:
                    value.length === 2 && value[1]
                        ? moment(value[1], this.inputFormat).format(
                              this.fieldSettingsParts.timeFormat,
                          )
                        : null,
            });
            return;
        }

        this.onChange(value);
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.value.disable();
            this.isInterval.disable();
            this.isDayAndNight.disable();
        } else {
            this.value.enable();
            this.isDayAndNight.enable();
            if (!this.isDayAndNight.value) {
                this.isInterval.enable();
            }
        }
    }

    public writeValue(value): void {
        // transform from parts to default timeinterval values
        if (!this.ngControl.control) return;
        if (this.fieldSettingsParts) {
            this.inputFormat = 'YYYY-MM-DD ' + this.fieldSettingsParts.timeFormat;
            if (value) {
                const fromName = this.fieldSettingsParts.fromTimeFieldName;
                const toName = this.fieldSettingsParts.toTimeFieldName;
                const from = value[fromName] ? '1970-01-01 ' + value[fromName] : null;
                const to = value[toName] ? '1970-01-01 ' + value[toName] : null;
                value = [from, to];
            }
        }

        if (!value || value.length === 0) {
            if (!this.defaultInterval) {
                this.isInterval.setValue([]);
            }
            this.value.setValue('');
            this.setCalendarValues();
            return;
        }
        const from = value[0];
        const to = value[1];

        const displayFromValue = moment(from, this.inputFormat);
        const displayToValue = moment(to, this.inputFormat);

        if (!displayFromValue.isValid()) {
            if (!this.defaultInterval) {
                this.isInterval.setValue([]);
            }
            return;
        }
        let formattedValue = displayFromValue.format(this.displayFormat);

        if (to && displayToValue.isValid()) {
            formattedValue += formattedValue ? this.delimiter : ``;
            formattedValue += displayToValue.format(this.displayFormat);
        }
        if (this.value.value !== formattedValue) {
            this.value.setValue(formattedValue);
        }
        if ((!to || !displayToValue.isValid()) && formattedValue) {
            this.isInterval.setValue([]);
        }
        this.setCalendarValues();
    }

    setValueFromTimeCalendar(value: string) {
        this.value.setValue(value);
        //this.showCalendar = false;
    }

    openTimeCalendar(e) {
        this.showCalendar = !this.showCalendar;
        if (this.instanceTimePopper) this.instanceTimePopper.update();
    }

    private _calculateDayAndNightValue() {
        const endOfTheDay = moment()
            .hours(23)
            .minutes(59)
            .seconds(59)
            .milliseconds(999)
            .format(this.displayFormat);
        const startOfTheDay = moment()
            .hours(0)
            .minutes(0)
            .seconds(0)
            .milliseconds(0)
            .format(this.displayFormat);
        return `${startOfTheDay}${this.delimiter}${endOfTheDay}`;
    }
}
